import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PoundSterling, Loader2, Lock } from 'lucide-react';
import { format, parseISO, isWeekend } from 'date-fns';
import {
  getJobCustomer, getJobLabel, getJobSpan, getPrintScaleClass,
  getActiveAuditorsWithJobs, getDateRange, getBankHoliday, leaveColors
} from '../../utils/reportHelpers';
import ReportPrintHeader from './ReportPrintHeader';
import { LockLegend } from '../JobLock';

export default function RevenueForecastReport({
  jobs, loading, auditors, startDate, endDate, bankHolidays,
  forecastAllocationDays, forecastAllocationAuditors,
  formatCurrency, reportTitle, region
}) {
  const dates = getDateRange(startDate, endDate);

  // Pre-calculate revenue allocation using settings-driven rules
  const auditorTotals = {};
  const dateTotals = {};
  let grandTotal = 0;

  // Initialize totals
  auditors.forEach(auditor => {
    auditorTotals[auditor.id] = 0;
  });

  dates.forEach(date => {
    const dateStr = format(date, 'yyyy-MM-dd');
    dateTotals[dateStr] = 0;
  });

  // Allocation tracking: jobId -> array of { auditorId, dateStr, amount, isFirst }
  const jobAllocations = new Map();

  // Calculate allocations based on settings
  jobs.forEach(job => {
    const jobId = job.id;
    const isLeave = job.event_type?.startsWith('LEAVE');

    // For leave events, still track them but with 0 revenue
    const price = isLeave ? 0 : (job.effective_forecast || job.site?.forecast_price || 0);

    // Include ALL jobs (even zero forecast) - they should still appear in reports
    // Leave events show with 0 revenue

    const jobAuditors = job.auditors || [];

    // For per-auditor allocation with assignments
    const hasAssignments = job.assignments && job.assignments.length > 0;

    // Use job.auditors as the source of truth for who is currently assigned
    // Filter out stale assignment records for auditors no longer on the job
    const activeAuditorIds = new Set(jobAuditors.map(a => a.id));

    if (activeAuditorIds.size === 0) return; // Must have at least one auditor

    // Calculate total days across all ACTIVE auditors for proper revenue split
    let totalAuditorDays = 0;
    if (hasAssignments) {
      job.assignments
        .filter(a => activeAuditorIds.has(a.auditor_id))
        .forEach(assignment => {
          const start = parseISO(assignment.start_date);
          const end = parseISO(assignment.end_date);
          totalAuditorDays += Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        });
      // Fallback: if no active assignments remain, use job span × auditor count
      if (totalAuditorDays === 0) {
        totalAuditorDays = getJobSpan(job) * activeAuditorIds.size;
      }
    } else {
      const span = getJobSpan(job);
      totalAuditorDays = span * activeAuditorIds.size;
    }

    // Process each ACTIVE auditor only
    activeAuditorIds.forEach(auditorId => {
      const auditor = jobAuditors.find(a => a.id === auditorId) || { id: auditorId };
      let auditorDates = [];
      let auditorDays = 0;

      if (hasAssignments) {
        // Find ALL assignments for this auditor (not just the first one)
        const auditorAssignments = job.assignments.filter(a => a.auditor_id === auditorId);
        if (auditorAssignments.length > 0) {
          // Collect all dates from all assignments
          auditorAssignments.forEach(assignment => {
            dates.forEach(date => {
              const dateStr = format(date, 'yyyy-MM-dd');
              if (dateStr >= assignment.start_date && dateStr <= assignment.end_date) {
                if (!auditorDates.includes(dateStr)) {
                  auditorDates.push(dateStr);
                }
              }
            });
            // Sum up days from each assignment
            const start = parseISO(assignment.start_date);
            const end = parseISO(assignment.end_date);
            auditorDays += Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
          });
          auditorDates.sort(); // Ensure dates are in order

          // Stale-assignment guard: assignments exist but ALL dates fall outside the
          // reporting period (e.g. job was rescheduled after batch creation).
          // Fall back to job-level dates so revenue is never silently dropped.
          // auditorDays is kept from the stale assignment so the proportional split
          // between co-auditors is preserved.
          if (auditorDates.length === 0) {
            const jobStart = job.scheduled_date;
            const jobEnd = job.end_date || job.scheduled_date;
            dates.forEach(date => {
              const dateStr = format(date, 'yyyy-MM-dd');
              if (dateStr >= jobStart && dateStr <= jobEnd) auditorDates.push(dateStr);
            });
            if (auditorDays === 0) auditorDays = getJobSpan(job);
          }
        } else {
          // Mismatch: auditor is listed in job.auditors but has no assignment records
          // (e.g. job was reassigned without updating job_auditors table).
          // Fall back to job-level date span so revenue is never silently dropped.
          const jobStart = job.scheduled_date;
          const jobEnd = job.end_date || job.scheduled_date;
          dates.forEach(date => {
            const dateStr = format(date, 'yyyy-MM-dd');
            if (dateStr >= jobStart && dateStr <= jobEnd) {
              auditorDates.push(dateStr);
            }
          });
          auditorDays = getJobSpan(job);
        }
      } else {
        // Legacy: use job-level dates for all auditors
        const jobStart = job.scheduled_date;
        const jobEnd = job.end_date || job.scheduled_date;
        dates.forEach(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          if (dateStr >= jobStart && dateStr <= jobEnd) {
            auditorDates.push(dateStr);
          }
        });
        auditorDays = getJobSpan(job);
      }

      if (auditorDates.length === 0) return;

      // Calculate this auditor's share based on days worked
      const auditorShare = totalAuditorDays > 0 ? (price * auditorDays) / totalAuditorDays : 0;

      if (forecastAllocationDays === 'first_day') {
        // Amount only on first day of this auditor's assignment
        auditorDates.forEach((dateStr, idx) => {
          const isFirstDay = idx === 0;
          const allocation = {
            auditorId: auditor.id,
            dateStr: dateStr,
            amount: isFirstDay ? auditorShare : 0,
            isFirst: isFirstDay
          };

          if (!jobAllocations.has(jobId)) {
            jobAllocations.set(jobId, []);
          }
          jobAllocations.get(jobId).push(allocation);

          if (isFirstDay) {
            auditorTotals[auditor.id] = (auditorTotals[auditor.id] || 0) + auditorShare;
            dateTotals[dateStr] = (dateTotals[dateStr] || 0) + auditorShare;
            grandTotal += auditorShare;
          }
        });
      } else {
        // Equal split across all days for this auditor
        const perDay = auditorDates.length > 0 ? auditorShare / auditorDates.length : 0;
        let remaining = auditorShare;

        auditorDates.forEach((dateStr, idx) => {
          const isLast = idx === auditorDates.length - 1;
          const dayAmount = isLast ? remaining : Math.round(perDay * 100) / 100;
          remaining -= dayAmount;

          const allocation = {
            auditorId: auditor.id,
            dateStr: dateStr,
            amount: dayAmount,
            isFirst: idx === 0
          };

          if (!jobAllocations.has(jobId)) {
            jobAllocations.set(jobId, []);
          }
          jobAllocations.get(jobId).push(allocation);

          auditorTotals[auditor.id] = (auditorTotals[auditor.id] || 0) + dayAmount;
          dateTotals[dateStr] = (dateTotals[dateStr] || 0) + dayAmount;
          grandTotal += dayAmount;
        });
      }
    });
  });

  // Helper to get allocation for a specific cell
  const getCellAllocations = (auditorId, dateStr) => {
    const result = [];
    jobAllocations.forEach((allocations, jobId) => {
      allocations.forEach(alloc => {
        if (alloc.auditorId === auditorId && alloc.dateStr === dateStr) {
          const job = jobs.find(j => j.id === jobId);
          const isLeave = job?.event_type?.startsWith('LEAVE');
          result.push({
            job,
            amount: alloc.amount,
            isFirst: alloc.isFirst,
            isLeave
          });
        }
      });
    });
    return result;
  };

  // Round totals for display (2dp)
  const roundedGrandTotal = Math.round(grandTotal * 100) / 100;

  // iter403 — Jobs with Revenue: count only unique jobs whose total
  // allocated amount is greater than zero. Excludes Leave events
  // (price = 0) and zero-forecast jobs which were inflating the KPI.
  let jobsWithRevenueCount = 0;
  jobAllocations.forEach(allocations => {
    const jobTotal = allocations.reduce((sum, a) => sum + (a.amount || 0), 0);
    if (jobTotal > 0) jobsWithRevenueCount += 1;
  });

  // Allocation mode description
  const allocationDesc = [];
  if (forecastAllocationDays === 'first_day') {
    allocationDesc.push('revenue on first day');
  } else {
    allocationDesc.push('revenue split across days');
  }
  if (forecastAllocationAuditors === 'first_auditor') {
    allocationDesc.push('first auditor only');
  } else {
    allocationDesc.push('split across auditors');
  }

  // Get only auditors with jobs in this period
  const activeAuditors = getActiveAuditorsWithJobs(jobs, auditors);
  const printScaleClass = getPrintScaleClass(activeAuditors.length);

  return (
    <div className={`space-y-6 print-section ${printScaleClass}`} data-testid="forecast">
      <ReportPrintHeader title={reportTitle} startDate={startDate} endDate={endDate} region={region} />

      <Card>
        <CardHeader className="print:hidden">
          <CardTitle className="flex items-center gap-2">
            <PoundSterling className="h-5 w-5" />
            Revenue Forecast
          </CardTitle>
          <CardDescription>
            Financial projections by date and auditor ({allocationDesc.join(', ')}).
            {activeAuditors.length < auditors.length && (
              <span className="ml-2 text-amber-600">
                (Showing {activeAuditors.length} auditors with jobs in this period)
              </span>
            )}
            <br />
            Grand Total: <span className="font-bold text-green-600">{formatCurrency(roundedGrandTotal)}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeAuditors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No auditors with jobs in the selected date range
            </div>
          ) : (
            <div className="overflow-x-auto print:overflow-visible">
              {/* Axis Label for Auditors - screen only */}
              <div className="flex items-center mb-2 print:hidden">
                <div className="w-[120px]"></div>
                <div className="flex-1 text-center">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider px-2 py-1 bg-slate-100 rounded">
                    &larr; Auditors &rarr;
                  </span>
                </div>
              </div>

              <div className="flex">
                {/* Axis Label for Date - screen only */}
                <div className="flex flex-col items-center justify-center pr-1 print:hidden">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    Dates &darr;
                  </span>
                </div>

              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="border p-2 bg-slate-100 text-left sticky left-0 z-10" style={{ width: '120px', minWidth: '120px' }}>
                      Date
                    </th>
                    {activeAuditors.map(auditor => (
                      <th
                        key={auditor.id}
                        className="border p-2 text-center bg-slate-50"
                        style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}
                      >
                        <div className="font-medium text-slate-800">{auditor.first_name}</div>
                        <div className="font-medium text-slate-800 text-xs">{auditor.last_name}</div>
                      </th>
                    ))}
                    <th className="border p-2 bg-slate-200 text-right" style={{ width: '100px', minWidth: '100px' }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dates.map(date => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const holiday = getBankHoliday(bankHolidays, dateStr);
                    const weekend = isWeekend(date);
                    const dateTotal = Math.round((dateTotals[dateStr] || 0) * 100) / 100;

                    return (
                      <tr key={dateStr}>
                        <td
                          className={`border p-2 sticky left-0 z-10 font-medium ${
                            holiday ? 'bg-amber-100' : weekend ? 'bg-slate-200' : 'bg-white'
                          }`}
                          style={{ width: '120px', minWidth: '120px' }}
                        >
                          <div className="flex flex-col">
                            <span>{format(date, 'EEE d MMM')}</span>
                            {holiday && (
                              <span className="text-[9px] text-amber-700 font-normal">{holiday.name}</span>
                            )}
                          </div>
                        </td>
                        {activeAuditors.map(auditor => {
                          const cellAllocations = getCellAllocations(auditor.id, dateStr);

                          return (
                            <td
                              key={auditor.id}
                              className={`border p-1 align-top ${
                                holiday ? 'bg-amber-50' : weekend ? 'bg-slate-100' : ''
                              }`}
                              style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}
                            >
                              {cellAllocations.map(({ job, amount, isFirst, isLeave }) => {
                                const customer = getJobCustomer(job);
                                const roundedAmount = Math.round(amount * 100) / 100;
                                const isLocked = !!job?.is_locked;

                                const cellStyle = isLeave
                                  ? {
                                      backgroundColor: leaveColors[job.event_type]?.bg || '#e5e7eb',
                                      borderLeft: `2px solid ${leaveColors[job.event_type]?.border || '#9ca3af'}`
                                    }
                                  : {
                                      backgroundColor: customer?.colour ? `${customer.colour}25` : '#e5e7eb',
                                      borderLeft: `2px solid ${customer?.colour || '#9ca3af'}`
                                    };

                                // iter340 v8e — locked cells get a 3px amber outline.
                                if (isLocked) {
                                  cellStyle.outline = '3px solid #f59e0b';
                                  cellStyle.outlineOffset = '-3px';
                                }

                                return (
                                  <div
                                    key={job.id}
                                    className={`job-cell rounded px-1 py-0.5 mb-0.5 text-[10px] ${
                                      isFirst ? 'font-medium' : 'opacity-60'
                                    }`}
                                    style={cellStyle}
                                    title={isLeave ? getJobLabel(job) : `${job.site?.name} - ${job.service?.name}${isLocked ? ' (Locked)' : ''}`}
                                    data-testid={isLocked ? `forecast-locked-cell-${job.id}` : undefined}
                                  >
                                    <div className="break-words leading-tight flex items-center gap-1">
                                      {isLocked && (
                                        <Lock className="h-2.5 w-2.5 text-amber-700 flex-shrink-0" aria-label="Locked" />
                                      )}
                                      <span className="break-words">{getJobLabel(job)}</span>
                                    </div>
                                    {roundedAmount > 0 && (
                                      <div className="text-green-700 font-bold currency">
                                        {formatCurrency(roundedAmount)}
                                      </div>
                                    )}
                                    {isLeave && (
                                      <div className="text-[9px] text-muted-foreground">
                                        (no revenue)
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </td>
                          );
                        })}
                        <td className={`border p-2 text-right font-medium currency ${
                          dateTotal > 0 ? 'text-green-700 bg-green-50' : 'text-muted-foreground'
                        }`}>
                          {dateTotal > 0 ? formatCurrency(dateTotal) : '\u2014'}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className="bg-slate-200 font-bold totals-row">
                    <td className="border p-2 sticky left-0 z-10 bg-slate-200">
                      TOTAL
                    </td>
                    {activeAuditors.map(auditor => {
                      const auditorTotal = Math.round((auditorTotals[auditor.id] || 0) * 100) / 100;
                      return (
                        <td key={auditor.id} className="border p-2 text-right currency">
                          {auditorTotal > 0 ? formatCurrency(auditorTotal) : '\u2014'}
                        </td>
                      );
                    })}
                    <td className="border p-2 text-right bg-green-100 text-green-800 currency">
                      {formatCurrency(roundedGrandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Summary - screen only */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Total Forecast</div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(roundedGrandTotal)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Jobs with Revenue</div>
            <div className="text-2xl font-bold">{jobsWithRevenueCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Avg per Job</div>
            <div className="text-2xl font-bold">
              {jobsWithRevenueCount > 0 ? formatCurrency(Math.round((roundedGrandTotal / jobsWithRevenueCount) * 100) / 100) : '\u2014'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Avg per Auditor</div>
            <div className="text-2xl font-bold">
              {auditors.length > 0 ? formatCurrency(Math.round((roundedGrandTotal / auditors.length) * 100) / 100) : '\u2014'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* iter340 v8e — lock legend */}
      <div className="print:hidden">
        <LockLegend />
      </div>
    </div>
  );
}
